import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarClock, Wrench, Plus, Check, X, Loader2, ChevronLeft, ChevronRight, List, Calendar as CalendarIcon, Camera, Trash2, Package, Pencil, MapPin, ImageOff, ZoomIn } from "lucide-react";

const LEAVE_TYPE_LABELS = { sick: 'Sick Leave', casual: 'Casual Leave', paid: 'Paid Leave', unpaid: 'Unpaid Leave', emergency: 'Emergency Leave' };
const TOOL_CONDITION_LABELS = { good: 'Good', fair: 'Fair', needs_repair: 'Needs Repair', out_of_service: 'Out of Service' };
const TOOL_CONDITION_STYLES = {
  good: 'bg-green-100 text-green-800', fair: 'bg-amber-100 text-amber-800',
  needs_repair: 'bg-orange-100 text-orange-800', out_of_service: 'bg-red-100 text-red-800'
};
const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800', cancelled: 'bg-gray-100 text-gray-600',
  ordered: 'bg-blue-100 text-blue-800', fulfilled: 'bg-emerald-100 text-emerald-800'
};
const URGENCY_STYLES = { low: 'bg-gray-100 text-gray-700', medium: 'bg-blue-100 text-blue-700', high: 'bg-red-100 text-red-700' };

const StatusBadge = ({ status }) => (
  <Badge className={STATUS_STYLES[status] || 'bg-gray-100 text-gray-700'} data-testid={`status-badge-${status}`}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </Badge>
);

const RequestLeaveForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date || !form.reason.trim()) {
      toast.error("Please fill in dates and a reason");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leave-requests`, form);
      toast.success("Leave request submitted");
      setOpen(false);
      setForm({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit leave request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="request-leave-btn"><Plus className="h-4 w-4 mr-2" />Request Leave</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Leave</DialogTitle>
          <DialogDescription>Submit a leave request for management to review.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Leave Type</Label>
            <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
              <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(LEAVE_TYPE_LABELS).map(([val, label]) => (
                  <SelectItem key={val} value={val}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="leave-start-date">Start Date</Label>
              <Input id="leave-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start-date-input" />
            </div>
            <div>
              <Label htmlFor="leave-end-date">End Date</Label>
              <Input id="leave-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end-date-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="leave-reason">Reason</Label>
            <Textarea id="leave-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why do you need this leave?" data-testid="leave-reason-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} data-testid="submit-leave-request-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CALENDAR_EMPLOYEE_COLORS = ['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-teal-500', 'bg-indigo-500', 'bg-rose-500'];

const getEmployeeColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return CALENDAR_EMPLOYEE_COLORS[Math.abs(hash) % CALENDAR_EMPLOYEE_COLORS.length];
};

const pad2 = (n) => n.toString().padStart(2, '0');

// Monthly calendar showing everyone's APPROVED leave — one colored chip per
// employee per day, clustered under each date cell.
const LeaveCalendarView = ({ requests }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const approved = requests.filter((r) => r.status === 'approved');

  const leaveByDate = {};
  approved.forEach((r) => {
    const cursor = new Date(r.start_date);
    const end = new Date(r.end_date);
    while (cursor <= end) {
      const key = `${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}-${pad2(cursor.getDate())}`;
      if (!leaveByDate[key]) leaveByDate[key] = [];
      leaveByDate[key].push({ employee_name: r.employee_name, leave_type: r.leave_type });
      cursor.setDate(cursor.getDate() + 1);
    }
  });

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startOffset + 1;
    cells.push(dayNum >= 1 && dayNum <= daysInMonth ? dayNum : null);
  }

  const today = new Date();
  const isToday = (dayNum) => dayNum && year === today.getFullYear() && month === today.getMonth() && dayNum === today.getDate();
  const monthLabel = currentMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <div data-testid="leave-calendar-view">
      <div className="flex items-center justify-between mb-3">
        <Button size="icon" variant="outline" onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} data-testid="leave-calendar-prev-month">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <p className="font-medium text-gray-900" data-testid="leave-calendar-month-label">{monthLabel}</p>
        <Button size="icon" variant="outline" onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} data-testid="leave-calendar-next-month">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((dayNum, idx) => {
          if (!dayNum) return <div key={idx} className="min-h-[84px] rounded border border-dashed border-gray-100 bg-gray-50" />;
          const key = `${year}-${pad2(month + 1)}-${pad2(dayNum)}`;
          const dayLeaves = leaveByDate[key] || [];
          return (
            <div
              key={idx}
              className={`min-h-[84px] rounded border p-1 ${isToday(dayNum) ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
              data-testid={`leave-calendar-day-${key}`}
            >
              <div className={`text-xs ${isToday(dayNum) ? 'font-bold text-blue-700' : 'text-gray-600'}`}>{dayNum}</div>
              <div className="space-y-0.5 mt-1">
                {dayLeaves.slice(0, 3).map((l, i) => (
                  <div
                    key={i}
                    className={`text-[10px] leading-tight text-white rounded px-1 py-0.5 truncate ${getEmployeeColor(l.employee_name)}`}
                    title={`${l.employee_name} — ${LEAVE_TYPE_LABELS[l.leave_type] || l.leave_type}`}
                  >
                    {l.employee_name.split(' ')[0]}
                  </div>
                ))}
                {dayLeaves.length > 3 && (
                  <div className="text-[10px] text-gray-500">+{dayLeaves.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {approved.length === 0 && (
        <p className="text-sm text-gray-500 text-center mt-4" data-testid="leave-calendar-empty">No approved leave to show yet.</p>
      )}
    </div>
  );
};

const LeaveRequestsPanel = ({ user, isManager }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [view, setView] = useState('list');

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leave-requests`);
      setRequests(res.data || []);
    } catch (error) {
      toast.error("Failed to load leave requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const review = async (id, status) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/leave-requests/${id}/review`, { status });
      toast.success(`Leave request ${status}`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review request");
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (id) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/leave-requests/${id}/cancel`);
      toast.success("Leave request cancelled");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">{requests.length} leave request{requests.length !== 1 ? 's' : ''} across the team</p>
          <div className="flex items-center rounded-lg border p-0.5">
            <Button
              size="sm"
              variant={view === 'list' ? 'default' : 'ghost'}
              className="h-7 px-2"
              onClick={() => setView('list')}
              data-testid="leave-view-list-btn"
            >
              <List className="h-3.5 w-3.5 mr-1" /> List
            </Button>
            <Button
              size="sm"
              variant={view === 'calendar' ? 'default' : 'ghost'}
              className="h-7 px-2"
              onClick={() => setView('calendar')}
              data-testid="leave-view-calendar-btn"
            >
              <CalendarIcon className="h-3.5 w-3.5 mr-1" /> Calendar
            </Button>
          </div>
        </div>
        {user?.employee_id ? (
          <RequestLeaveForm onCreated={fetchRequests} />
        ) : (
          <span className="text-xs text-gray-400 italic">Your account isn't linked to an employee profile</span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
      ) : view === 'calendar' ? (
        <LeaveCalendarView requests={requests} />
      ) : requests.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-gray-500" data-testid="no-leave-requests">No leave requests yet.</CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="leave-requests-list">
          {requests.map((r) => (
            <Card key={r.id} data-testid={`leave-request-${r.id}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.employee_name}</span>
                      <Badge variant="outline">{LEAVE_TYPE_LABELS[r.leave_type] || r.leave_type}</Badge>
                      <StatusBadge status={r.status} />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {r.start_date} → {r.end_date} ({r.days_count} day{r.days_count !== 1 ? 's' : ''})
                    </p>
                    <p className="text-sm text-gray-700 mt-1 italic">"{r.reason}"</p>
                    {r.review_notes && <p className="text-xs text-gray-500 mt-1">Review note: {r.review_notes}</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {isManager && r.status === 'pending' && (
                      <>
                        <Button size="sm" variant="outline" className="text-green-700 border-green-300" disabled={busyId === r.id} onClick={() => review(r.id, 'approved')} data-testid={`approve-leave-${r.id}`}>
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-700 border-red-300" disabled={busyId === r.id} onClick={() => review(r.id, 'rejected')} data-testid={`reject-leave-${r.id}`}>
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {r.employee_id === user?.employee_id && r.status === 'pending' && (
                      <Button size="sm" variant="ghost" disabled={busyId === r.id} onClick={() => cancel(r.id)} data-testid={`cancel-leave-${r.id}`}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const RequestToolForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ tool_name: '', description: '', quantity: 1, estimated_cost: '', urgency: 'medium' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tool_name.trim()) {
      toast.error("Please enter the tool name");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tool-requests`, {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        estimated_cost: form.estimated_cost ? parseFloat(form.estimated_cost) : null
      });
      toast.success("Tool request submitted");
      setOpen(false);
      setForm({ tool_name: '', description: '', quantity: 1, estimated_cost: '', urgency: 'medium' });
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit tool request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="request-tool-btn"><Plus className="h-4 w-4 mr-2" />Request Tool</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request a New Tool</DialogTitle>
          <DialogDescription>Ask management to purchase a tool you need for the job.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="tool-name">Tool Name</Label>
            <Input id="tool-name" value={form.tool_name} onChange={(e) => setForm({ ...form, tool_name: e.target.value })} placeholder="e.g., OBD2 Diagnostic Scanner" data-testid="tool-name-input" />
          </div>
          <div>
            <Label htmlFor="tool-description">Description / Justification</Label>
            <Textarea id="tool-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Why do you need this tool?" data-testid="tool-description-input" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="tool-quantity">Quantity</Label>
              <Input id="tool-quantity" type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="tool-quantity-input" />
            </div>
            <div>
              <Label htmlFor="tool-cost">Est. Cost (₹)</Label>
              <Input id="tool-cost" type="number" min="0" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} placeholder="Optional" data-testid="tool-cost-input" />
            </div>
            <div>
              <Label>Urgency</Label>
              <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                <SelectTrigger data-testid="tool-urgency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} data-testid="submit-tool-request-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ToolRequestsPanel = ({ user, isManager }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tool-requests`);
      setRequests(res.data || []);
    } catch (error) {
      toast.error("Failed to load tool requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const review = async (id, status) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/tool-requests/${id}/review`, { status });
      toast.success(`Tool request marked ${status}`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review request");
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (id) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/tool-requests/${id}/cancel`);
      toast.success("Tool request cancelled");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{requests.length} tool request{requests.length !== 1 ? 's' : ''} across the team</p>
        {user?.employee_id ? (
          <RequestToolForm onCreated={fetchRequests} />
        ) : (
          <span className="text-xs text-gray-400 italic">Your account isn't linked to an employee profile</span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
      ) : requests.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-gray-500" data-testid="no-tool-requests">No tool requests yet.</CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="tool-requests-list">
          {requests.map((r) => (
            <Card key={r.id} data-testid={`tool-request-${r.id}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.employee_name}</span>
                      <Badge variant="outline">{r.tool_name} x{r.quantity}</Badge>
                      <Badge className={URGENCY_STYLES[r.urgency]}>{r.urgency} urgency</Badge>
                      <StatusBadge status={r.status} />
                    </div>
                    {r.description && <p className="text-sm text-gray-700 mt-1 italic">"{r.description}"</p>}
                    {r.estimated_cost != null && <p className="text-sm text-gray-600 mt-1">Est. cost: ₹{r.estimated_cost.toLocaleString('en-IN')}</p>}
                    {r.review_notes && <p className="text-xs text-gray-500 mt-1">Review note: {r.review_notes}</p>}
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    {isManager && r.status === 'pending' && (
                      <>
                        <Button size="sm" variant="outline" className="text-green-700 border-green-300" disabled={busyId === r.id} onClick={() => review(r.id, 'approved')} data-testid={`approve-tool-${r.id}`}>
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-700 border-red-300" disabled={busyId === r.id} onClick={() => review(r.id, 'rejected')} data-testid={`reject-tool-${r.id}`}>
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {isManager && r.status === 'approved' && (
                      <Button size="sm" variant="outline" className="text-blue-700 border-blue-300" disabled={busyId === r.id} onClick={() => review(r.id, 'ordered')} data-testid={`order-tool-${r.id}`}>
                        Mark Ordered
                      </Button>
                    )}
                    {isManager && r.status === 'ordered' && (
                      <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300" disabled={busyId === r.id} onClick={() => review(r.id, 'fulfilled')} data-testid={`fulfill-tool-${r.id}`}>
                        Mark Fulfilled
                      </Button>
                    )}
                    {r.employee_id === user?.employee_id && r.status === 'pending' && (
                      <Button size="sm" variant="ghost" disabled={busyId === r.id} onClick={() => cancel(r.id)} data-testid={`cancel-tool-${r.id}`}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const ToolFormDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const isEdit = !!initial?.id;
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ tool_name: '', category: '', description: '', condition: 'good', quantity: 1, location: '' });

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        tool_name: initial.tool_name || '', category: initial.category || '', description: initial.description || '',
        condition: initial.condition || 'good', quantity: initial.quantity ?? 1, location: initial.location || ''
      } : { tool_name: '', category: '', description: '', condition: 'good', quantity: 1, location: '' });
    }
  }, [open, initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tool_name.trim()) {
      toast.error("Please enter the tool name");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, quantity: parseInt(form.quantity) || 1 };
      if (isEdit) {
        await axios.put(`${API}/tools-inventory/${initial.id}`, payload);
        toast.success("Tool updated");
      } else {
        await axios.post(`${API}/tools-inventory`, payload);
        toast.success("Tool added to inventory");
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save tool");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Tool' : 'Add Tool to Inventory'}</DialogTitle>
          <DialogDescription>Track a physical tool currently used in the garage, including what it's used for.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="inv-tool-name">Tool Name</Label>
              <Input id="inv-tool-name" value={form.tool_name} onChange={(e) => setForm({ ...form, tool_name: e.target.value })} placeholder="e.g., Hydraulic Jack" data-testid="inventory-tool-name-input" />
            </div>
            <div>
              <Label htmlFor="inv-tool-category">Category</Label>
              <Input id="inv-tool-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g., Lifting Equipment" data-testid="inventory-tool-category-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="inv-tool-description">What it's used for</Label>
            <Textarea id="inv-tool-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Purpose / notes about this tool" data-testid="inventory-tool-description-input" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Condition</Label>
              <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                <SelectTrigger data-testid="inventory-tool-condition-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TOOL_CONDITION_LABELS).map(([val, label]) => (
                    <SelectItem key={val} value={val}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="inv-tool-quantity">Quantity</Label>
              <Input id="inv-tool-quantity" type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="inventory-tool-quantity-input" />
            </div>
            <div>
              <Label htmlFor="inv-tool-location">Location</Label>
              <Input id="inv-tool-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g., Bay 2 shelf" data-testid="inventory-tool-location-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} data-testid="submit-inventory-tool-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {isEdit ? 'Save Changes' : 'Add Tool'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ToolInventoryCard = ({ tool, isManager, onChanged, onOpenLightbox }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    setUploading(true);
    try {
      await axios.post(`${API}/tools-inventory/${tool.id}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success("Photo(s) uploaded");
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoIndex) => {
    try {
      await axios.delete(`${API}/tools-inventory/${tool.id}/photos/${photoIndex}`);
      toast.success("Photo removed");
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete photo");
    }
  };

  const handleDeleteTool = async () => {
    if (!window.confirm(`Remove "${tool.tool_name}" from inventory?`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/tools-inventory/${tool.id}`);
      toast.success("Tool removed from inventory");
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete tool");
    } finally {
      setDeleting(false);
    }
  };

  const photos = tool.photos || [];

  return (
    <Card data-testid={`tool-inventory-card-${tool.id}`}>
      <CardContent className="py-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{tool.tool_name}</span>
              {tool.category && <Badge variant="outline">{tool.category}</Badge>}
              <Badge className={TOOL_CONDITION_STYLES[tool.condition] || 'bg-gray-100 text-gray-700'} data-testid={`tool-condition-badge-${tool.id}`}>
                {TOOL_CONDITION_LABELS[tool.condition] || tool.condition}
              </Badge>
              <Badge variant="outline">Qty: {tool.quantity}</Badge>
            </div>
            {tool.description && <p className="text-sm text-gray-700 mt-1 italic">"{tool.description}"</p>}
            {tool.location && (
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> {tool.location}</p>
            )}
            {tool.added_by && <p className="text-xs text-gray-400 mt-1">Added by {tool.added_by}</p>}
          </div>
          {isManager && (
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => setFormOpen(true)} data-testid={`edit-tool-${tool.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="text-red-600" disabled={deleting} onClick={handleDeleteTool} data-testid={`delete-tool-${tool.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {photos.length > 0 ? (
          <div className="flex flex-wrap gap-2" data-testid={`tool-photos-${tool.id}`}>
            {photos.map((p, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={`${API}/tools-inventory/${tool.id}/photos/${idx}`}
                  alt={p.filename || 'tool photo'}
                  className="h-16 w-16 object-cover rounded border cursor-pointer"
                  onClick={() => onOpenLightbox(tool, idx)}
                  data-testid={`tool-photo-thumb-${tool.id}-${idx}`}
                />
                <button
                  type="button"
                  className="absolute -top-1 -right-1 bg-white rounded-full shadow border p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onOpenLightbox(tool, idx)}
                  data-testid={`tool-photo-zoom-${tool.id}-${idx}`}
                >
                  <ZoomIn className="h-3 w-3 text-gray-600" />
                </button>
                {isManager && (
                  <button
                    type="button"
                    className="absolute -bottom-1 -right-1 bg-white rounded-full shadow border p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDeletePhoto(idx)}
                    data-testid={`tool-photo-delete-${tool.id}-${idx}`}
                  >
                    <X className="h-3 w-3 text-red-600" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 flex items-center gap-1" data-testid={`tool-no-photos-${tool.id}`}>
            <ImageOff className="h-3 w-3" /> No photos yet
          </p>
        )}

        {isManager && (
          <div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid={`tool-photo-upload-input-${tool.id}`} />
            <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid={`tool-photo-upload-btn-${tool.id}`}>
              {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Camera className="h-3.5 w-3.5 mr-1.5" />}
              Add Photos
            </Button>
          </div>
        )}
      </CardContent>
      <ToolFormDialog open={formOpen} onOpenChange={setFormOpen} initial={tool} onSaved={onChanged} />
    </Card>
  );
};

const PhotoLightbox = ({ tool, photoIndex, onClose }) => {
  if (!tool) return null;
  const photos = tool.photos || [];
  return (
    <Dialog open={!!tool} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="tool-photo-lightbox">
        <DialogHeader>
          <DialogTitle>{tool.tool_name}</DialogTitle>
          <DialogDescription>{photos[photoIndex]?.filename || ''}</DialogDescription>
        </DialogHeader>
        <img
          src={`${API}/tools-inventory/${tool.id}/photos/${photoIndex}`}
          alt={photos[photoIndex]?.filename || 'tool photo'}
          className="w-full max-h-[70vh] object-contain rounded"
        />
      </DialogContent>
    </Dialog>
  );
};

const ToolsInventoryPanel = ({ isManager }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [lightbox, setLightbox] = useState({ tool: null, photoIndex: 0 });

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tools-inventory`);
      setItems(res.data || []);
    } catch (error) {
      toast.error("Failed to load tools inventory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return (
    <div className="space-y-4 mt-4" data-testid="tools-inventory-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">{items.length} tool{items.length !== 1 ? 's' : ''} currently in the garage</p>
        {isManager ? (
          <Button onClick={() => setAddOpen(true)} data-testid="add-inventory-tool-btn"><Plus className="h-4 w-4 mr-2" />Add Tool</Button>
        ) : (
          <span className="text-xs text-gray-400 italic">Only management can add or edit tools</span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-gray-500" data-testid="no-inventory-tools">
          <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          No tools in inventory yet.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="tools-inventory-list">
          {items.map((tool) => (
            <ToolInventoryCard
              key={tool.id}
              tool={tool}
              isManager={isManager}
              onChanged={fetchItems}
              onOpenLightbox={(t, idx) => setLightbox({ tool: t, photoIndex: idx })}
            />
          ))}
        </div>
      )}

      <ToolFormDialog open={addOpen} onOpenChange={setAddOpen} initial={null} onSaved={fetchItems} />
      <PhotoLightbox tool={lightbox.tool} photoIndex={lightbox.photoIndex} onClose={() => setLightbox({ tool: null, photoIndex: 0 })} />
    </div>
  );
};

export const LeaveAndToolsSection = () => {
  const { user } = useAuth();
  const isManager = user?.role === 'management' || user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState('leave');

  return (
    <div className="space-y-6" data-testid="leave-and-tools-section">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarClock className="h-6 w-6" /> Leave &amp; Tools
        </h2>
        <p className="text-sm text-gray-500 mt-1">Request leave, see your team's time off, and request tools for the job.</p>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="leave" data-testid="leave-tab-trigger">
            <CalendarClock className="h-4 w-4 mr-2" /> Leave Requests
          </TabsTrigger>
          <TabsTrigger value="tools" data-testid="tools-tab-trigger">
            <Wrench className="h-4 w-4 mr-2" /> Tool Requests
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="inventory-tab-trigger">
            <Package className="h-4 w-4 mr-2" /> Tools Inventory
          </TabsTrigger>
        </TabsList>
        <TabsContent value="leave">
          <LeaveRequestsPanel user={user} isManager={isManager} />
        </TabsContent>
        <TabsContent value="tools">
          <ToolRequestsPanel user={user} isManager={isManager} />
        </TabsContent>
        <TabsContent value="inventory">
          <ToolsInventoryPanel isManager={isManager} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
